<template>
    <div class="flex-table" :class="wrapperClass">
        <header v-if="showHeader" :style="headerStyle">
            <div v-if="hasHeaderLeftSlot" class="header-left">
                <slot name="header-left"></slot>
            </div>

            <search-filter
                v-if="searchable"
                ref="search"
                v-model="searchText"
                @keypress-enter="$emit('search-keypress-enter')"
            />

            <div v-if="hasHeaderRightSlot" class="header-right">
                <slot name="header-right"></slot>
            </div>
        </header>

        <table v-table-header-scrollbar-spacer>
            <thead>
                <tr>
                    <th v-if="checkboxesEnabled" class="flex-table-checkbox">
                        <input
                            v-if="checkAllEnabled"
                            type="checkbox"
                            @change="onCheckAllChange"
                        />
                    </th>

                    <table-column
                        v-for="column in columns"
                        :key="column.name"
                        :column="column"
                        :rows="displayRows"
                        :sort="sort"
                        @sort="onColumnSort"
                    />
                </tr>
            </thead>
            <tbody :style="tableBodyStyle">
                <tr v-if="loading">
                    <td
                        class="flex-table-loading"
                        :colspan="columns.length + (checkboxesEnabled ? 1 : 0)"
                    >
                        <i class="fa-solid fa-spinner fa-spin" />
                    </td>
                </tr>
                <tr v-else-if="displayRows.length === 0">
                    <td
                        class="flex-table-empty"
                        :colspan="columns.length + (checkboxesEnabled ? 1 : 0)"
                    />
                </tr>
                <template v-else>
                    <table-row
                        v-for="row in displayRows"
                        :key="row.id"
                        :columns="columns"
                        :row="row"
                        :checkboxes-enabled="checkboxesEnabled"
                        :checked="row.isChecked()"
                        :selected="selectedRowId === row.id"
                        :row-checkbox-enabled="checkboxIsEnabledForRow(row)"
                        :row-click-enabled="rowClickEnabled"
                        @click="onRowClick"
                        @checkbox-change="onRowCheckboxChange"
                    />
                </template>
            </tbody>
        </table>

        <div v-show="false">
            <slot></slot>
        </div>
    </div>
</template>

<script>
import TableColumn from './components/TableColumn';
import TableRow from './components/TableRow';
import TableCell from './components/TableCell';
import Column from './models/Column';
import Row from './models/Row';
import SearchFilter from './components/SearchFilter';
import { flipOrder } from './util/helpers';
import sortBy from 'lodash-es/sortBy';
import { onWrapperHeightChange } from 'larasnap/hooks';

export default {
    components: {
        TableColumn,
        TableRow,
        /* eslint-disable vue/no-unused-components */
        TableCell, // Used by TableRow functional component
        SearchFilter,
    },
    props: {
        data: { type: Array, default: () => [] },
        idField: { type: String, default: 'id' },
        sortBy: { type: String, default: '' },
        sortOrder: { type: String, default: 'asc' },
        sortable: { type: Boolean, default: true },
        searchable: { type: Boolean, default: true },
        searchWidth: { type: Number, default: 300 },
        checkboxesEnabled: { type: Boolean, default: false },
        checkAllEnabled: { type: Boolean, default: true },
        checkboxIsEnabledForRow: { type: Function, default: () => true },
        rowClickEnabled: { type: Boolean, default: false },
        rowSelectEnabled: { type: Boolean, default: false },
        defaultSelectedRowIndex: { type: Number, default: -1 },
        checkRowOnClick: { type: Boolean, default: false },
        height: { type: Number, default: null },
        wrapperClass: { type: String, default: '' },
        showHeader: { type: Boolean, default: true },
        fluidHeight: { type: Object, null: 200 },
        loading: { type: Boolean, default: false },
    },
    data() {
        return {
            sort: {
                name: '',
                order: 'asc',
            },
            columns: [],
            rows: [],
            searchText: '',
            checkedRowIds: [],
            lastRowCheckedIndex: null,
            shiftKeyDown: false,
            selectedRowId: null,
            contentWrapperHeight: null,
        };
    },
    computed: {
        hasHeaderLeftSlot() {
            return this.$slots['header-left'];
        },
        hasHeaderRightSlot() {
            return this.$slots['header-right'];
        },
        headerStyle() {
            return (
                'grid-template-columns: ' +
                [
                    ...(this.hasHeaderLeftSlot ? ['[col] auto'] : []),
                    `[col] ${this.searchWidth}px`,
                    ...(this.hasHeaderRightSlot ? ['[col] auto'] : []),
                ].join(' ')
            );
        },
        displayRows() {
            let rows = this.rows.filter(x =>
                x.passesSearchFilter(this.searchText),
            );

            if (this.sort.name !== '') {
                rows = sortBy(rows, row => row.sortData[this.sort.name]);
                if (this.sort.order === 'desc') {
                    rows = rows.reverse();
                }
            }

            rows.forEach((row, index) => (row.index = index));

            return rows;
        },
        tableBodyHeight() {
            if (this.fluidHeight) {
                if (this.contentWrapperHeight) {
                    return this.contentWrapperHeight - this.fluidHeight.offset;
                } else {
                    return this.fluidHeight.default;
                }
            } else {
                return this.height;
            }
        },
        tableBodyStyle() {
            return this.tableBodyHeight
                ? { 'max-height': `${this.tableBodyHeight}px` }
                : {};
        },
    },
    watch: {
        data: {
            deep: true,
            handler() {
                this.createRows();
            },
        },

        checkedRowIds(ids) {
            this.$emit('row-check', ids);
        },
        displayRows() {
            this.lastRowCheckedIndex = null;
        },
    },
    created() {
        this.$on('check-row', this.setRowCheck);

        const setShiftKeyState = state => e => {
            if (e.keyCode === 16) this.shiftKeyDown = state;
        };
        const onKeyDown = setShiftKeyState(true);
        const onKeyUp = setShiftKeyState(false);

        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('keyup', onKeyUp);

        this.$once('hook:destroyed', () => {
            document.removeEventListener('keydown', onKeyDown);
            document.addEventListener('keyup', onKeyUp);
        });

        onWrapperHeightChange(height => (this.contentWrapperHeight = height));
    },
    mounted() {
        if (this.$slots.default) {
            this.columns = this.$slots.default
                .filter(x => x.componentInstance)
                .map((x, i) => Column(x.componentInstance, this, i));
        }

        const sortColumn = this.columns.find(x => x.sort);

        if (sortColumn) {
            this.sort.name = sortColumn.name;
            this.sort.order = sortColumn.sort;
        } else {
            this.sort.name = this.sortBy;
            this.sort.order = this.sortOrder;
        }

        this.createRows();

        if (this.defaultSelectedRowIndex >= 0) {
            this.selectedRowId =
                this.displayRows[this.defaultSelectedRowIndex].id;
        }
    },
    methods: {
        onCheckAllChange(e) {
            this.checkAll(e.target.checked);
        },
        checkAll(isChecked) {
            this.checkedRowIds = isChecked
                ? this.rows
                      .filter(x => this.checkboxIsEnabledForRow(x))
                      .map(x => x.id)
                : [];
        },
        setRowCheck(row, isChecked) {
            if (isChecked) {
                if (!row.isChecked()) {
                    this.checkedRowIds.push(row.id);
                }
            } else {
                this.checkedRowIds = this.checkedRowIds.filter(
                    x => x !== row.id,
                );
            }
        },
        onRowCheckboxChange(row, isChecked) {
            // Check rows by range if shift key is being held down
            if (this.shiftKeyDown && this.lastRowCheckedIndex !== null) {
                const [start, end] = [
                    row.index,
                    this.lastRowCheckedIndex,
                ].sort();
                this.displayRows
                    .slice(start, end + 1)
                    .filter(this.checkboxIsEnabledForRow)
                    .forEach(row => row.check(isChecked));
                // Otherwise check the individual row
            } else {
                row.check(isChecked);
            }

            this.lastRowCheckedIndex = row.index;
        },
        onRowClick(row) {
            if (this.rowClickEnabled) {
                this.$emit('row-click', row);

                if (this.checkRowOnClick) {
                    this.onRowCheckboxChange(row, !row.isChecked());
                }
                if (this.rowSelectEnabled) {
                    this.selectedRowId = row.id;
                }
            }
        },
        createRows() {
            this.rows = this.data.map(x => Row(x, this));
            this.columns.forEach(x => (x.rows = this.rows));

            // Remove any checkedRowIds that belong to now non-existant rows
            const rowIds = this.rows.map(x => x.id);
            this.checkedRowIds = this.checkedRowIds.filter(id =>
                rowIds.includes(id),
            );
        },
        getRows() {
            return this.rows;
        },
        getDisplayRows() {
            return this.displayRows;
        },
        clearCheckedRows() {
            this.checkedRowIds = [];
        },
        clearSearch() {
            if (this.$refs.search) {
                this.$refs.search.clear();
            }
        },
        focusSearch() {
            if (this.$refs.search) {
                this.$refs.search.focus();
            }
        },
        onSearchChange(results) {
            this.searchText = results;
        },
        onColumnSort(column) {
            if (this.sort.name === column.name) {
                this.sort.order = flipOrder(this.sort.order);
            } else {
                this.sort.name = column.name;
            }
        },
    },
};
</script>

<style lang="scss">
@import './css/flex-table.scss';
</style>
